import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CalendarDays, Check, X, Plus, Users, FileDown, ChevronLeft, ChevronRight,
  Clock, Home, Plane, Stethoscope, LogOut as EarlyIcon, Pencil, UserX, UserCheck, Paperclip, Bell, MapPin,
} from "lucide-react";
import { api } from "../api";
import { useAuth } from "../auth";

const TYPES = [
  ["annual", "Yıllık İzin"], ["daily", "Günlük İzin"], ["half_day", "Yarım Günlük İzin"],
  ["hourly", "Saatlik İzin"], ["excuse", "Mazeret İzni"], ["unpaid", "Ücretsiz İzin"],
  ["remote", "Uzaktan Çalışma"], ["business_trip", "İş Seyahati"], ["late_arrival", "Sabah Geç Gelme"],
  ["early_leave", "Erken Çıkma"], ["sick", "Hastalık/Rapor"],
];
const TYPE_LABEL = Object.fromEntries(TYPES);
const WD = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];

const monday = (d) => { const x = new Date(d); const n = (x.getDay() + 6) % 7; x.setDate(x.getDate() - n); return x.toISOString().slice(0, 10); };
const addDays = (iso, n) => { const x = new Date(iso); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };
const todayISO = () => new Date().toISOString().slice(0, 10);
const trDate = (iso) => iso ? new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short" }) : "";

async function blobDownload(url, filename) {
  try {
    const r = await api.get(url, { responseType: "blob" });
    const u = URL.createObjectURL(r.data);
    const a = document.createElement("a"); a.href = u; a.download = filename; a.click();
    URL.revokeObjectURL(u);
  } catch { toast.error("İndirme başarısız"); }
}

// ---------------------------------------------------------------------------
// Bildirim çanı
// ---------------------------------------------------------------------------
function NotificationsBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const load = () => api.get("/leave/notifications").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const unread = items.filter((i) => !i.read).length;
  const markAll = async () => { await api.post("/leave/notifications/read-all"); load(); };
  return (
    <div className="notif-wrap">
      <button className="notif-btn" onClick={() => setOpen(!open)} data-testid="notif-bell">
        <Bell size={18} />{unread > 0 && <span className="notif-badge" data-testid="notif-badge">{unread}</span>}
      </button>
      {open && (
        <div className="notif-pop" data-testid="notif-pop">
          <div className="notif-head"><b>Bildirimler</b>{unread > 0 && <button className="link-btn" onClick={markAll} data-testid="notif-readall">Tümünü okundu yap</button>}</div>
          {items.length === 0 && <div className="muted" style={{ padding: 12 }}>Bildirim yok.</div>}
          {items.slice(0, 25).map((n) => (
            <div key={n.id} className={`notif-item ${n.read ? "" : "unread"}`}><span>{n.message}</span><small>{trDate((n.created_at || "").slice(0, 10))}</small></div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Ortak takvim ızgarası
// ---------------------------------------------------------------------------
function CalendarGrid({ data }) {
  if (!data) return <div className="muted" style={{ padding: 20 }}>Yükleniyor…</div>;
  const cell = (empId, day) => {
    if (day.is_holiday) return { cls: "cal-holiday", txt: "Tatil", title: day.holiday_name };
    if (day.is_weekend) return { cls: "cal-weekend", txt: "" };
    const c = (data.cells[empId] || {})[day.date];
    if (!c) return { cls: "cal-office", txt: "Ofis" };
    const map = { remote: ["cal-remote", "Uzaktan"], trip: ["cal-trip", "Seyahat"], late: ["cal-late", "Geç"], early: ["cal-early", "Erken"], sick: ["cal-sick", "Rapor"], leave: ["cal-leave", c.label || "İzin"] };
    const [cls, txt] = map[c.status] || ["cal-office", "Ofis"];
    let label = txt;
    if ((c.status === "late" || c.status === "early") && c.time) label = `${txt} ${c.time}`;
    const title = c.status === "trip" ? `İş Seyahati · ${c.destination || ""}${c.trip_project ? " · " + c.trip_project : ""}` : c.label;
    return { cls, txt: label, title };
  };
  return (
    <div className="cal-wrap" data-testid="leave-calendar-grid">
      <table className="cal-table">
        <thead>
          <tr>
            <th className="cal-emp-h">Çalışan</th>
            {data.days.map((d) => (
              <th key={d.date} className={d.is_weekend ? "cal-wknd-h" : ""}>
                <span>{WD[d.weekday]}</span><small>{trDate(d.date)}</small>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.employees.length === 0 && <tr><td colSpan={data.days.length + 1} className="muted" style={{ padding: 18 }}>Aktif çalışan yok.</td></tr>}
          {data.employees.map((e) => (
            <tr key={e.id}>
              <td className="cal-emp" title={e.department}><b>{e.name}</b>{e.department && <small>{e.department}</small>}</td>
              {data.days.map((d) => {
                const c = cell(e.id, d);
                return <td key={d.date} className={`cal-cell ${c.cls}`} title={c.title || ""}>{c.txt}</td>;
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Görsel durum panosu (günlük + haftalık)
// ---------------------------------------------------------------------------
const BOARD_BUCKETS = [
  ["office", "Ofiste", "bk-office", Home],
  ["remote", "Uzaktan", "bk-remote", Home],
  ["trip", "İş Seyahati", "bk-trip", MapPin],
  ["leave", "İzinli", "bk-leave", Plane],
  ["late", "Geç Gelen", "bk-late", Clock],
  ["early", "Erken Çıkan", "bk-early", EarlyIcon],
  ["sick", "Raporlu", "bk-sick", Stethoscope],
];
const statusOf = (dm, c) => dm.is_holiday ? "holiday" : dm.is_weekend ? "weekend" : (c ? c.status : "office");

function StatusBoard({ data }) {
  const [focus, setFocus] = useState(null);
  if (!data) return <div className="muted" style={{ padding: 20 }}>Yükleniyor…</div>;
  const days = data.days || [];
  const fday = days.find((d) => d.date === focus) || days.find((d) => d.date === todayISO()) || days[0];
  const emps = data.employees || [];

  const dayCount = (dm) => {
    const r = { office: 0, remote: 0, trip: 0, leave: 0, late: 0, early: 0, sick: 0 };
    if (dm.is_weekend || dm.is_holiday) return r;
    for (const e of emps) { const st = statusOf(dm, (data.cells[e.id] || {})[dm.date]); if (r[st] !== undefined) r[st]++; else r.office++; }
    return r;
  };

  const buckets = {}; BOARD_BUCKETS.forEach(([k]) => (buckets[k] = []));
  if (fday && !fday.is_weekend && !fday.is_holiday) {
    for (const e of emps) {
      const c = (data.cells[e.id] || {})[fday.date];
      const st = statusOf(fday, c);
      const key = buckets[st] !== undefined ? st : "office";
      buckets[key].push({ name: e.name, dep: e.department, time: c?.time, dest: c?.destination, proj: c?.trip_project, label: c?.label });
    }
  }

  return (
    <div data-testid="status-board">
      <div className="board-week" data-testid="board-week">
        {days.map((d) => {
          const cnt = dayCount(d);
          const active = fday && d.date === fday.date;
          return (
            <button key={d.date} className={`board-day ${active ? "active" : ""} ${d.is_weekend ? "wknd" : ""} ${d.is_holiday ? "hol" : ""}`} onClick={() => setFocus(d.date)} data-testid={`board-day-${d.date}`}>
              <span className="bd-top"><b>{WD[d.weekday]}</b><small>{trDate(d.date)}</small></span>
              {d.is_holiday ? <span className="bd-note">{d.holiday_name || "Tatil"}</span>
                : d.is_weekend ? <span className="bd-note">Hafta sonu</span>
                : <span className="bd-badges">
                    {cnt.office > 0 && <i className="bd-b bk-office" title="Ofiste">{cnt.office}</i>}
                    {cnt.remote > 0 && <i className="bd-b bk-remote" title="Uzaktan">{cnt.remote}</i>}
                    {cnt.trip > 0 && <i className="bd-b bk-trip" title="Seyahat">{cnt.trip}</i>}
                    {cnt.leave > 0 && <i className="bd-b bk-leave" title="İzinli">{cnt.leave}</i>}
                    {cnt.late > 0 && <i className="bd-b bk-late" title="Geç">{cnt.late}</i>}
                    {cnt.early > 0 && <i className="bd-b bk-early" title="Erken">{cnt.early}</i>}
                    {cnt.sick > 0 && <i className="bd-b bk-sick" title="Rapor">{cnt.sick}</i>}
                  </span>}
            </button>
          );
        })}
      </div>

      <h3 className="section-title" style={{ marginTop: 18 }}>{fday ? `${WD[fday.weekday]}, ${trDate(fday.date)} — Günlük Durum` : "Durum"}</h3>
      {fday && (fday.is_weekend || fday.is_holiday) ? (
        <div className="muted" style={{ padding: 16 }}>{fday.is_holiday ? (fday.holiday_name || "Resmî tatil") : "Hafta sonu"} — çalışma günü değil.</div>
      ) : (
        <div className="board-grid">
          {BOARD_BUCKETS.map(([k, label, cls, Icon]) => (
            <div key={k} className={`board-col ${cls}`} data-testid={`board-col-${k}`}>
              <div className="board-col-head"><Icon size={15} /> <b>{label}</b> <span className="board-col-count">{buckets[k].length}</span></div>
              <div className="board-col-body">
                {buckets[k].length === 0 && <span className="muted" style={{ fontSize: 12 }}>—</span>}
                {buckets[k].map((p, i) => (
                  <div key={i} className="board-chip" title={p.dep || ""}>
                    <b>{p.name}</b>
                    {(k === "late" || k === "early") && p.time && <small>{p.time}</small>}
                    {k === "trip" && (p.dest || p.proj) && <small>{p.dest}{p.proj ? ` · ${p.proj}` : ""}</small>}
                    {k === "leave" && p.label && <small>{p.label}</small>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SummaryCards({ s, pending }) {
  if (!s) return null;
  const cards = [
    ["Toplam", s.total, "sc-total", Users], ["Ofiste", s.office, "sc-office", Home],
    ["Uzaktan", s.remote, "sc-remote", Home], ["İzinli", s.on_leave, "sc-leave", Plane],
    ["Geç Gelen", s.late, "sc-late", Clock], ["Erken Çıkan", s.early, "sc-early", EarlyIcon],
    ["Rapor", s.sick, "sc-sick", Stethoscope], ["Seyahat", s.trip, "sc-trip", MapPin], ["Onay Bekleyen", pending, "sc-pending", Check],
  ];
  return (
    <div className="leave-cards" data-testid="leave-summary-cards">
      {cards.map(([label, val, cls, Icon]) => (
        <div key={label} className={`leave-card ${cls}`}><Icon size={16} /><div><b>{val}</b><span>{label}</span></div></div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Talep oluşturma modalı
// ---------------------------------------------------------------------------
function RequestModal({ close, onSaved, employees }) {
  const [f, setF] = useState({ type: "annual", start_date: todayISO(), end_date: todayISO(), start_time: "", end_time: "", reason: "", user_id: "", destination: "", trip_project: "", trip_project_id: "" });
  const [doc, setDoc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [projects, setProjects] = useState([]);
  useEffect(() => { api.get("/leave/projects").then((r) => setProjects(r.data)).catch(() => {}); }, []);
  const rangeTime = f.type === "hourly";
  const singleTime = ["late_arrival", "early_leave"].includes(f.type);
  const single = ["half_day", "hourly", "late_arrival", "early_leave", "sick"].includes(f.type);
  const isTrip = f.type === "business_trip";
  const timeLabel = f.type === "late_arrival" ? "Tahmini Geliş Saati" : "Tahmini Çıkış Saati";

  const submit = async () => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("type", f.type);
      fd.append("start_date", f.start_date);
      fd.append("end_date", single ? f.start_date : f.end_date);
      if (f.start_time) fd.append("start_time", f.start_time);
      if (rangeTime && f.end_time) fd.append("end_time", f.end_time);
      fd.append("reason", f.reason);
      if (isTrip) { fd.append("destination", f.destination); fd.append("trip_project", f.trip_project); if (f.trip_project_id) fd.append("trip_project_id", f.trip_project_id); }
      if (employees && f.user_id) fd.append("user_id", f.user_id);
      if (doc) fd.append("document", doc);
      await api.post("/leave/requests", fd);
      toast.success("Talep oluşturuldu");
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Talep oluşturulamadı"); }
    finally { setBusy(false); }
  };

  return (
    <div className="modal-backdrop" onClick={close}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="leave-request-modal">
        <h3><CalendarDays size={18} /> Yeni İzin / Çalışma Talebi</h3>
        <div className="form-grid">
          {employees && (
            <label>Çalışan<select value={f.user_id} onChange={(e) => setF({ ...f, user_id: e.target.value })} data-testid="req-employee">
              <option value="">— Seçin —</option>
              {employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
            </select></label>
          )}
          <label>Talep Türü<select value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} data-testid="req-type">
            {TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select></label>
          <label>{single ? "Tarih" : "Başlangıç"}<input type="date" value={f.start_date} onChange={(e) => setF({ ...f, start_date: e.target.value })} data-testid="req-start" /></label>
          {!single && <label>Bitiş<input type="date" value={f.end_date} onChange={(e) => setF({ ...f, end_date: e.target.value })} data-testid="req-end" /></label>}
          {rangeTime && <label>Başlangıç Saati<input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} data-testid="req-start-time" /></label>}
          {rangeTime && <label>Bitiş Saati<input type="time" value={f.end_time} onChange={(e) => setF({ ...f, end_time: e.target.value })} data-testid="req-end-time" /></label>}
          {singleTime && <label>{timeLabel}<input type="time" value={f.start_time} onChange={(e) => setF({ ...f, start_time: e.target.value })} data-testid="req-single-time" /></label>}
          {isTrip && <label>Nereye (Varış Yeri)<input value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} placeholder="Örn. Manisa / Gördes" data-testid="req-destination" /></label>}
          {isTrip && <label>Proje<select value={f.trip_project_id} onChange={(e) => { const p = projects.find((x) => x.id === e.target.value); setF({ ...f, trip_project_id: e.target.value, trip_project: p ? p.name : "" }); }} data-testid="req-project">
            <option value="">— Proje Seçin —</option>
            {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select></label>}
          <label className="full">Açıklama<textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="req-reason" /></label>
          {f.type === "sick" && (
            <label className="full"><Paperclip size={13} /> Belge (rapor) <input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setDoc(e.target.files[0])} data-testid="req-document" /></label>
          )}
        </div>
        <div className="modal-actions">
          <button className="ghost-btn" onClick={close}>Vazgeç</button>
          <button className="new-btn" disabled={busy} onClick={submit} data-testid="req-submit">{busy ? "Gönderiliyor…" : "Talep Oluştur"}</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Çalışan görünümü
// ---------------------------------------------------------------------------
function EmployeeLeave() {
  const [me, setMe] = useState(null);
  const [reqs, setReqs] = useState([]);
  const [cal, setCal] = useState(null);
  const [modal, setModal] = useState(false);
  const [view, setView] = useState("pano");
  const [start] = useState(monday(todayISO()));

  const load = () => {
    api.get("/leave/me").then((r) => setMe(r.data)).catch(() => {});
    api.get("/leave/requests").then((r) => setReqs(r.data)).catch(() => {});
    api.get(`/leave/calendar?start=${start}&days=7`).then((r) => setCal(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const b = me?.balance || {};

  const cancel = async (id) => {
    try { const r = await api.post(`/leave/requests/${id}/cancel`, {}); toast.success(r.data.status || "İşlendi"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "İptal edilemedi"); }
  };

  return (
    <main className="content" data-testid="page-leave">
      <div className="page-head"><h1>İzin & Çalışma Takvimi</h1>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <NotificationsBell />
          <button className="new-btn" onClick={() => setModal(true)} data-testid="employee-new-request"><Plus size={17} /> Yeni Talep</button>
        </div>
      </div>
      <div className="leave-cards" data-testid="employee-balance">
        <div className="leave-card sc-total"><div><b>{b.total ?? "—"}</b><span>Toplam Hak (gün)</span></div></div>
        <div className="leave-card sc-office"><div><b>{b.carryover ?? 0}</b><span>Devreden</span></div></div>
        <div className="leave-card sc-leave"><div><b>{b.used ?? 0}</b><span>Kullanılan</span></div></div>
        <div className="leave-card sc-pending"><div><b>{b.pending ?? 0}</b><span>Bekleyen</span></div></div>
        <div className="leave-card sc-remote"><div><b>{b.remaining ?? "—"}</b><span>Kalan</span></div></div>
      </div>

      <h3 className="section-title">Taleplerim</h3>
      <div className="table-wrap">
        <table className="data-table" data-testid="employee-requests">
          <thead><tr><th>Tür</th><th>Başlangıç</th><th>Bitiş</th><th>Durum</th><th>Açıklama</th><th></th></tr></thead>
          <tbody>
            {reqs.length === 0 && <tr><td colSpan={6} className="muted">Henüz talebiniz yok.</td></tr>}
            {reqs.map((r) => (
              <tr key={r.id}>
                <td>{r.type_label}</td><td>{trDate(r.start_date)}</td><td>{trDate(r.end_date)}</td>
                <td><span className={`status ${r.status === "Onaylandı" ? "status-green" : r.status === "Reddedildi" ? "status-red" : r.status === "Beklemede" ? "status-amber" : "status-blue"}`}>{r.status}</span>{r.cancel_requested && <small className="muted"> (iptal talebi)</small>}</td>
                <td className="mono-small">{r.type === "business_trip" ? `${r.destination || ""}${r.trip_project ? ` · ${r.trip_project}` : ""}` : (r.reject_reason ? `Ret: ${r.reject_reason}` : r.reason)}</td>
                <td className="row-actions">{r.document && <button className="row-action" title="Belgeyi aç" onClick={() => blobDownload(`/leave/requests/${r.id}/document`, r.document.filename || "belge")} data-testid={`doc-${r.id}`}><Paperclip size={12} /></button>}{["Beklemede", "Onaylandı"].includes(r.status) && <button className="row-action danger" onClick={() => cancel(r.id)} data-testid={`cancel-${r.id}`}>İptal</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="page-head" style={{ marginTop: 6 }}>
        <h3 className="section-title" style={{ margin: 0 }}>Ekip Durumu (bu hafta)</h3>
        <div className="tabs" data-testid="employee-view-tabs" style={{ border: "none", margin: 0, padding: 0 }}>
          <button className={view === "pano" ? "tab-active" : ""} onClick={() => setView("pano")} data-testid="emp-tab-pano">Durum Panosu</button>
          <button className={view === "takvim" ? "tab-active" : ""} onClick={() => setView("takvim")} data-testid="emp-tab-takvim">Takvim</button>
        </div>
      </div>
      {view === "pano" ? <StatusBoard data={cal} /> : <CalendarGrid data={cal} />}
      {modal && <RequestModal close={() => setModal(false)} onSaved={() => { setModal(false); load(); }} />}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Yönetici görünümü
// ---------------------------------------------------------------------------
function ManagerLeave() {
  const [tab, setTab] = useState("takvim");
  const [start, setStart] = useState(monday(todayISO()));
  const [days, setDays] = useState(7);
  const [dep, setDep] = useState("");
  const [ftype, setFtype] = useState("");
  const [cal, setCal] = useState(null);
  const [deps, setDeps] = useState([]);
  const [pending, setPending] = useState([]);
  const [emps, setEmps] = useState([]);
  const [annual, setAnnual] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [empModal, setEmpModal] = useState(null);
  const [reqModal, setReqModal] = useState(false);

  const loadCal = () => api.get(`/leave/calendar?start=${start}&days=${days}${dep ? `&department=${dep}` : ""}${ftype ? `&type=${ftype}` : ""}`).then((r) => setCal(r.data)).catch(() => {});
  const loadPending = () => api.get("/leave/requests/pending").then((r) => setPending(r.data)).catch(() => {});
  const loadEmps = () => api.get("/leave/employees?include_passive=true").then((r) => setEmps(r.data)).catch(() => {});
  const loadAll = () => { loadCal(); loadPending(); loadEmps(); api.get("/leave/departments").then((r) => setDeps(r.data)).catch(() => {}); };

  useEffect(() => { loadCal(); }, [start, days, dep, ftype]);
  useEffect(() => { loadPending(); loadEmps(); api.get("/leave/departments").then((r) => setDeps(r.data)).catch(() => {}); }, []);
  useEffect(() => { if (tab === "raporlar") { api.get("/leave/reports/annual").then((r) => setAnnual(r.data)).catch(() => {}); api.get("/leave/holidays").then((r) => setHolidays(r.data)).catch(() => {}); } }, [tab]);

  const decide = async (id, action, reason) => {
    try { await api.post(`/leave/requests/${id}/${action}`, action === "reject" ? { reason } : {}); toast.success(action === "approve" ? "Onaylandı" : "Reddedildi"); loadPending(); loadCal(); loadEmps(); }
    catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
  };

  return (
    <main className="content" data-testid="page-leave">
      <div className="page-head"><h1>İzin & Çalışma Takvimi</h1>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <NotificationsBell />
          <button className="new-btn" onClick={() => setReqModal(true)} data-testid="manager-new-request"><Plus size={17} /> Talep Ekle</button>
        </div>
      </div>

      <div className="tabs" data-testid="leave-tabs">
        {[["takvim", "Haftalık Takvim"], ["pano", "Durum Panosu"], ["onaylar", `Onay Bekleyenler${pending.length ? ` (${pending.length})` : ""}`], ["calisanlar", "Çalışanlar"], ["raporlar", "Raporlar & Tatiller"]].map(([k, l]) => (
          <button key={k} className={tab === k ? "tab-active" : ""} onClick={() => setTab(k)} data-testid={`tab-${k}`}>{l}</button>
        ))}
      </div>

      {tab === "takvim" && (
        <>
          <SummaryCards s={cal?.summary} pending={cal?.pending_count} />
          <div className="filter-bar">
            <button className="ghost-btn" onClick={() => setStart(addDays(start, -days))} data-testid="cal-prev"><ChevronLeft size={15} /></button>
            <button className="ghost-btn" onClick={() => setStart(monday(todayISO()))}>Bugün</button>
            <button className="ghost-btn" onClick={() => setStart(addDays(start, days))} data-testid="cal-next"><ChevronRight size={15} /></button>
            <select className="filter-select" value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="cal-view">
              <option value={1}>Gün</option><option value={7}>Hafta</option><option value={30}>Ay</option>
            </select>
            <select className="filter-select" value={dep} onChange={(e) => setDep(e.target.value)} data-testid="cal-dep">
              <option value="">Tüm departmanlar</option>{deps.map((d) => <option key={d}>{d}</option>)}
            </select>
            <select className="filter-select" value={ftype} onChange={(e) => setFtype(e.target.value)} data-testid="cal-type">
              <option value="">Tüm türler</option>{TYPES.map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <CalendarGrid data={cal} />
        </>
      )}

      {tab === "pano" && (
        <>
          <div className="filter-bar">
            <button className="ghost-btn" onClick={() => setStart(addDays(start, -7))} data-testid="board-prev"><ChevronLeft size={15} /></button>
            <button className="ghost-btn" onClick={() => { setDays(7); setStart(monday(todayISO())); }}>Bu Hafta</button>
            <button className="ghost-btn" onClick={() => setStart(addDays(start, 7))} data-testid="board-next"><ChevronRight size={15} /></button>
            <select className="filter-select" value={dep} onChange={(e) => setDep(e.target.value)} data-testid="board-dep">
              <option value="">Tüm departmanlar</option>{deps.map((d) => <option key={d}>{d}</option>)}
            </select>
          </div>
          <StatusBoard data={cal} />
        </>
      )}

      {tab === "onaylar" && (
        <div className="table-wrap">
          <table className="data-table" data-testid="pending-table">
            <thead><tr><th>Çalışan</th><th>Tür</th><th>Başlangıç</th><th>Bitiş</th><th>Açıklama</th><th>Oluşturma</th><th></th></tr></thead>
            <tbody>
              {pending.length === 0 && <tr><td colSpan={7} className="muted">Onay bekleyen talep yok.</td></tr>}
              {pending.map((r) => (
                <tr key={r.id}>
                  <td><b>{r.user_name}</b></td><td>{r.type_label}</td><td>{trDate(r.start_date)}</td><td>{trDate(r.end_date)}</td>
                  <td className="mono-small">{r.type === "business_trip" ? `${r.destination || ""}${r.trip_project ? ` · ${r.trip_project}` : ""}` : r.reason}</td><td className="mono-small">{trDate((r.created_at || "").slice(0, 10))}</td>
                  <td className="row-actions">
                    <button className="row-action" onClick={() => decide(r.id, "approve")} data-testid={`approve-${r.id}`}><Check size={14} /></button>
                    <button className="row-action danger" onClick={() => { const rs = window.prompt("Ret gerekçesi:"); if (rs !== null) decide(r.id, "reject", rs); }} data-testid={`reject-${r.id}`}><X size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "calisanlar" && (
        <>
          <div className="page-head" style={{ marginTop: 4 }}><h3 className="section-title" style={{ margin: 0 }}>Çalışanlar</h3>
            <button className="new-btn" onClick={() => setEmpModal({})} data-testid="add-employee"><Plus size={16} /> Çalışan Ekle</button></div>
          <div className="table-wrap">
            <table className="data-table" data-testid="employees-table">
              <thead><tr><th>Ad Soyad</th><th>Departman</th><th>İşe Başlama</th><th>Toplam</th><th>Kullanılan</th><th>Bekleyen</th><th>Kalan</th><th>Durum</th><th></th></tr></thead>
              <tbody>
                {emps.map((e) => (
                  <tr key={e.id} className={e.active === false ? "row-muted" : ""}>
                    <td><b>{e.name}</b><small className="muted"> {e.email}</small></td><td>{e.department}</td><td>{e.hire_date}</td>
                    <td>{e.balance?.total}</td><td>{e.balance?.used}</td><td>{e.balance?.pending}</td><td><b>{e.balance?.remaining}</b></td>
                    <td>{e.active === false ? <span className="status status-red">Pasif</span> : <span className="status status-green">Aktif</span>}</td>
                    <td className="row-actions">
                      <button className="row-action" onClick={() => setEmpModal(e)} data-testid={`edit-emp-${e.id}`}><Pencil size={13} /></button>
                      <button className="row-action" onClick={async () => { await api.post(`/leave/employees/${e.id}/active?active=${e.active === false}`); loadEmps(); }} data-testid={`toggle-emp-${e.id}`}>{e.active === false ? <UserCheck size={13} /> : <UserX size={13} />}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === "raporlar" && (
        <>
          <div className="page-head" style={{ marginTop: 4 }}><h3 className="section-title" style={{ margin: 0 }}>Yıllık İzin Bakiye Raporu</h3>
            <div className="row-actions">
              <button className="ghost-btn" onClick={() => blobDownload("/leave/reports/export?report=annual&fmt=excel", "yillik_izin.xlsx")} data-testid="export-annual-excel"><FileDown size={14} /> Excel</button>
              <button className="ghost-btn" onClick={() => blobDownload("/leave/reports/export?report=annual&fmt=pdf", "yillik_izin.pdf")} data-testid="export-annual-pdf"><FileDown size={14} /> PDF</button>
              <button className="ghost-btn" onClick={() => blobDownload("/leave/reports/export?report=requests&fmt=excel", "talepler.xlsx")}><FileDown size={14} /> Talepler (Excel)</button>
            </div>
          </div>
          <div className="table-wrap">
            <table className="data-table"><thead><tr><th>Ad Soyad</th><th>Departman</th><th>Hak Ediş</th><th>Devir</th><th>Toplam</th><th>Kullanılan</th><th>Bekleyen</th><th>Kalan</th></tr></thead>
              <tbody>{annual.map((r, i) => <tr key={i}><td><b>{r.name}</b></td><td>{r.department}</td><td>{r.entitlement}</td><td>{r.carryover}</td><td>{r.total}</td><td>{r.used}</td><td>{r.pending}</td><td><b>{r.remaining}</b></td></tr>)}</tbody>
            </table>
          </div>
          <h3 className="section-title">Resmî Tatiller</h3>
          <HolidayManager holidays={holidays} reload={() => api.get("/leave/holidays").then((r) => setHolidays(r.data))} />
        </>
      )}

      {empModal && <EmployeeModal emp={empModal} close={() => setEmpModal(null)} onSaved={() => { setEmpModal(null); loadEmps(); }} />}
      {reqModal && <RequestModal close={() => setReqModal(false)} onSaved={() => { setReqModal(false); loadCal(); loadPending(); loadEmps(); }} employees={emps.filter((e) => e.active !== false)} />}
    </main>
  );
}

function HolidayManager({ holidays, reload }) {
  const [f, setF] = useState({ date: todayISO(), name: "" });
  const add = async () => { if (!f.name) return; await api.post("/leave/holidays", f); setF({ date: todayISO(), name: "" }); reload(); };
  const del = async (id) => { if (!window.confirm("Bu tatili silmek istiyor musunuz?")) return; try { await api.delete(`/leave/holidays/${id}`); toast.success("Silindi"); reload(); } catch { toast.error("Silinemedi"); } };
  return (
    <div className="holiday-box" data-testid="holiday-manager">
      <div className="filter-bar">
        <input type="date" className="filter-select" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <input className="filter-select" style={{ minWidth: 220 }} placeholder="Tatil adı" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="holiday-name" />
        <button className="new-btn" onClick={add} data-testid="add-holiday"><Plus size={15} /> Ekle</button>
      </div>
      <div className="chip-list">
        {holidays.map((h) => <span key={h.id} className="chip">{trDate(h.date)} · {h.name} <X size={12} onClick={() => del(h.id)} style={{ cursor: "pointer" }} /></span>)}
      </div>
    </div>
  );
}

function EmployeeModal({ emp, close, onSaved }) {
  const isEdit = !!emp.id;
  const [f, setF] = useState({
    name: emp.name || "", email: emp.email || "", password: "", department: emp.department || "",
    hire_date: emp.hire_date || todayISO(), annual_override: emp.annual_override ?? "", carryover_days: emp.carryover_days ?? 0, phone: emp.phone || "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      const body = { ...f, annual_override: f.annual_override === "" ? null : Number(f.annual_override), carryover_days: Number(f.carryover_days) || 0 };
      if (isEdit) await api.put(`/leave/employees/${emp.id}`, body);
      else await api.post("/leave/employees", body);
      toast.success("Kaydedildi"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };
  return (
    <div className="modal-backdrop" onClick={close}>
      <div className="modal" onClick={(e) => e.stopPropagation()} data-testid="employee-modal">
        <h3><Users size={18} /> {isEdit ? "Çalışan Düzenle" : "Yeni Çalışan"}</h3>
        <div className="form-grid">
          <label>Ad Soyad<input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="emp-name" /></label>
          <label>E-posta<input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="emp-email" /></label>
          <label>{isEdit ? "Yeni Şifre (boş=değişmez)" : "Şifre"}<input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="emp-password" /></label>
          <label>Departman<input value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} data-testid="emp-dep" /></label>
          <label>İşe Başlama<input type="date" value={f.hire_date} onChange={(e) => setF({ ...f, hire_date: e.target.value })} data-testid="emp-hire" /></label>
          <label>Yıllık Hak (boş=kanuni)<input type="number" value={f.annual_override} onChange={(e) => setF({ ...f, annual_override: e.target.value })} placeholder="Otomatik" /></label>
          <label>Devreden Gün<input type="number" value={f.carryover_days} onChange={(e) => setF({ ...f, carryover_days: e.target.value })} /></label>
          <label>Telefon<input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} /></label>
        </div>
        <div className="modal-actions">
          <button className="ghost-btn" onClick={close}>Vazgeç</button>
          <button className="new-btn" disabled={busy} onClick={save} data-testid="emp-save">{busy ? "Kaydediliyor…" : "Kaydet"}</button>
        </div>
      </div>
    </div>
  );
}

export default function Leave() {
  const { user } = useAuth();
  const isManager = user?.role === "admin" || user?.is_super_admin;
  return isManager ? <ManagerLeave /> : <EmployeeLeave />;
}
